import React, { useEffect, useMemo, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import toast from 'react-hot-toast';
import {
  AREA_OF_NEED_LABELS,
  migrateApdrCyclesToSupportPlan,
  newRowId,
  DEFAULT_ACADEMIC_YEAR,
} from '../lib/apdrSchemas';
import { suggestApdrCycleContent } from '../lib/functions';

const ACHIEVED_OPTIONS = ['', 'Achieved', 'Partially', 'Not yet'];

function cycleHasContent(c) {
  return (c.rows?.length > 0) || c.legacyNotes || c.legacyOutcome;
}

function normalisePlan(student) {
  if (student?.apdrSupportPlan?.cycles?.length) {
    const plan = {
      academicYear: student.apdrSupportPlan.academicYear || DEFAULT_ACADEMIC_YEAR(),
      cycles: student.apdrSupportPlan.cycles.map((c, idx) => ({
        cycleNumber: c.cycleNumber ?? idx + 1,
        term: c.term || ['Autumn', 'Spring', 'Summer'][idx] || `Term ${idx + 1}`,
        fromDate: c.fromDate || '',
        toDate: c.toDate || '',
        assessSummary: c.assessSummary || '',
        rows: Array.isArray(c.rows) ? c.rows : [],
        reviewDate: c.reviewDate || '',
        reviewedBy: c.reviewedBy || '',
        legacyOutcome: c.legacyOutcome || '',
        legacyNotes: c.legacyNotes || '',
      })),
    };
    if (plan.cycles.some(cycleHasContent)) return plan;
  }
  return migrateApdrCyclesToSupportPlan(student?.apdrCycles, DEFAULT_ACADEMIC_YEAR());
}

function defaultActiveCycle(cycles) {
  const withContent = cycles.find(cycleHasContent);
  return withContent?.cycleNumber ?? 1;
}

export default function CyclesOfSupportPanel({ student, onSaved }) {
  const initialPlan = useMemo(() => normalisePlan(student), [student]);
  const [plan, setPlan] = useState(initialPlan);
  const [activeCycle, setActiveCycle] = useState(() => defaultActiveCycle(initialPlan.cycles));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    setPlan(initialPlan);
    setActiveCycle(defaultActiveCycle(initialPlan.cycles));
  }, [initialPlan]);

  const cycle = plan.cycles.find((c) => c.cycleNumber === activeCycle) || plan.cycles[0];

  const handleCycleField = (field, value) => {
    setPlan((prev) => ({
      ...prev,
      cycles: prev.cycles.map((c) => (c.cycleNumber === activeCycle ? { ...c, [field]: value } : c)),
    }));
  };

  const handleRowField = (rowId, field, value) => {
    setPlan((prev) => ({
      ...prev,
      cycles: prev.cycles.map((c) => {
        if (c.cycleNumber !== activeCycle) return c;
        return {
          ...c,
          rows: c.rows.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)),
        };
      }),
    }));
  };

  const handleInterventionField = (rowId, idx, field, value) => {
    setPlan((prev) => ({
      ...prev,
      cycles: prev.cycles.map((c) => {
        if (c.cycleNumber !== activeCycle) return c;
        return {
          ...c,
          rows: c.rows.map((r) => {
            if (r.id !== rowId) return r;
            const interventions = [...(r.interventions || [])];
            interventions[idx] = { ...interventions[idx], [field]: value };
            return { ...r, interventions };
          }),
        };
      }),
    }));
  };

  const addRow = () => {
    setPlan((prev) => ({
      ...prev,
      cycles: prev.cycles.map((c) => {
        if (c.cycleNumber !== activeCycle) return c;
        return {
          ...c,
          rows: [
            ...c.rows,
            {
              id: newRowId(),
              areaOfNeed: 'cognitionLearning',
              areaLabel: '',
              assessNotes: '',
              smartTarget: '',
              interventions: [{ name: '', frequency: '', duration: '', ratio: '', sourceType: 'manual' }],
              outcome: '',
              achieved: '',
            },
          ],
        };
      }),
    }));
    setEditing(true);
  };

  const removeRow = (rowId) => {
    setPlan((prev) => ({
      ...prev,
      cycles: prev.cycles.map((c) => {
        if (c.cycleNumber !== activeCycle) return c;
        return { ...c, rows: c.rows.filter((r) => r.id !== rowId) };
      }),
    }));
  };

  const addInterventionToRow = (rowId) => {
    setPlan((prev) => ({
      ...prev,
      cycles: prev.cycles.map((c) => {
        if (c.cycleNumber !== activeCycle) return c;
        return {
          ...c,
          rows: c.rows.map((r) => {
            if (r.id !== rowId) return r;
            return {
              ...r,
              interventions: [...(r.interventions || []), { name: '', frequency: '', duration: '', ratio: '', sourceType: 'manual' }],
            };
          }),
        };
      }),
    }));
  };

  const handleImport = async () => {
    if (!student?.id) return;
    setImporting(true);
    try {
      const result = await suggestApdrCycleContent({
        studentId: student.id,
        cycleNumber: activeCycle,
        fromDate: cycle.fromDate,
        toDate: cycle.toDate,
      });
      const suggestedRows = result?.rows || [];
      if (!suggestedRows.length) {
        toast('No live data found for this cycle period.', { icon: 'ℹ️' });
        return;
      }
      setPlan((prev) => ({
        ...prev,
        cycles: prev.cycles.map((c) => {
          if (c.cycleNumber !== activeCycle) return c;
          const merged = [...c.rows];
          suggestedRows.forEach((sr) => {
            const exists = merged.some((r) => r.smartTarget === sr.smartTarget && r.areaOfNeed === sr.areaOfNeed);
            if (!exists) merged.push({ ...sr, id: sr.id || newRowId() });
          });
          return { ...c, rows: merged };
        }),
      }));
      setEditing(true);
      toast.success(`Imported ${suggestedRows.length} suggested row(s)`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    if (!student?.id) {
      toast.error('Cannot save: student id missing.');
      return;
    }
    try {
      setSaving(true);
      await updateDoc(doc(db, 'students', student.id), {
        apdrSupportPlan: plan,
        apdrSupportPlanUpdatedAt: serverTimestamp(),
      });
      toast.success('Cycles of support saved');
      setEditing(false);
      onSaved?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not save cycles');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPlan(initialPlan);
    setEditing(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle>Cycles of support (APDR)</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Three terms of assess–plan–do–review evidence for EHCP requests. Academic year {plan.academicYear}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!editing ? (
            <>
              <button type="button" onClick={() => setEditing(true)} className="rounded border px-3 py-1.5 text-sm hover:bg-gray-50">Edit</button>
              <button type="button" onClick={handleImport} disabled={importing} className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
                {importing ? 'Importing...' : 'Import from live data'}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={handleCancel} className="rounded border px-3 py-1.5 text-sm">Cancel</button>
              <button type="button" onClick={handleSave} disabled={saving} className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          {plan.cycles.map((c) => (
            <button
              key={c.cycleNumber}
              type="button"
              onClick={() => setActiveCycle(c.cycleNumber)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium border ${
                activeCycle === c.cycleNumber
                  ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {c.term || `Cycle ${c.cycleNumber}`}
            </button>
          ))}
        </div>

        {editing && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <label>
              From
              <input type="date" value={cycle.fromDate || ''} onChange={(e) => handleCycleField('fromDate', e.target.value)} className="mt-1 w-full rounded border px-2 py-1" />
            </label>
            <label>
              To
              <input type="date" value={cycle.toDate || ''} onChange={(e) => handleCycleField('toDate', e.target.value)} className="mt-1 w-full rounded border px-2 py-1" />
            </label>
            <label>
              Review date
              <input type="date" value={cycle.reviewDate || ''} onChange={(e) => handleCycleField('reviewDate', e.target.value)} className="mt-1 w-full rounded border px-2 py-1" />
            </label>
            <label>
              Reviewed by
              <input value={cycle.reviewedBy || ''} onChange={(e) => handleCycleField('reviewedBy', e.target.value)} className="mt-1 w-full rounded border px-2 py-1" />
            </label>
          </div>
        )}

        {(cycle.legacyNotes || cycle.legacyOutcome) && !cycle.rows?.length && (
          <div className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            <p className="font-medium">Legacy cycle notes</p>
            {cycle.legacyOutcome && <p>Outcome: {cycle.legacyOutcome}</p>}
            {cycle.legacyNotes && <p>{cycle.legacyNotes}</p>}
          </div>
        )}

        {cycle.rows?.length === 0 ? (
          <p className="text-sm text-gray-500">No rows for this cycle yet. Import from live data or add a row.</p>
        ) : (
          <div className="space-y-4">
            {cycle.rows.map((row) => (
              <div key={row.id} className="rounded-lg border border-gray-200 p-3 space-y-2 dark:border-gray-700">
                <div className="grid md:grid-cols-2 gap-2">
                  <label className="text-xs">
                    Area of need
                    {editing ? (
                      <select value={row.areaOfNeed} onChange={(e) => handleRowField(row.id, 'areaOfNeed', e.target.value)} className="mt-1 w-full rounded border px-2 py-1 text-sm">
                        {Object.entries(AREA_OF_NEED_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-sm mt-1">{AREA_OF_NEED_LABELS[row.areaOfNeed] || row.areaOfNeed}</p>
                    )}
                  </label>
                  <label className="text-xs">
                    Area label (optional)
                    {editing ? (
                      <input value={row.areaLabel || ''} onChange={(e) => handleRowField(row.id, 'areaLabel', e.target.value)} className="mt-1 w-full rounded border px-2 py-1 text-sm" placeholder="e.g. (C&L) Writing" />
                    ) : (
                      <p className="text-sm mt-1">{row.areaLabel || 'Not recorded'}</p>
                    )}
                  </label>
                </div>
                <label className="text-xs block">
                  Assess (attainment / specialist advice)
                  {editing ? (
                    <textarea value={row.assessNotes || ''} onChange={(e) => handleRowField(row.id, 'assessNotes', e.target.value)} rows={2} className="mt-1 w-full rounded border px-2 py-1 text-sm" />
                  ) : (
                    <p className="text-sm mt-1 whitespace-pre-wrap">{row.assessNotes || 'Not recorded'}</p>
                  )}
                </label>
                <label className="text-xs block">
                  SMART target
                  {editing ? (
                    <textarea value={row.smartTarget || ''} onChange={(e) => handleRowField(row.id, 'smartTarget', e.target.value)} rows={2} className="mt-1 w-full rounded border px-2 py-1 text-sm" />
                  ) : (
                    <p className="text-sm mt-1 whitespace-pre-wrap">{row.smartTarget || 'Not recorded'}</p>
                  )}
                </label>

                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">How will this be achieved?</p>
                  {(row.interventions || []).map((int, idx) => (
                    <div key={idx} className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                      {editing ? (
                        <>
                          <input placeholder="Name" value={int.name || ''} onChange={(e) => handleInterventionField(row.id, idx, 'name', e.target.value)} className="rounded border px-2 py-1 text-sm" />
                          <input placeholder="Frequency" value={int.frequency || ''} onChange={(e) => handleInterventionField(row.id, idx, 'frequency', e.target.value)} className="rounded border px-2 py-1 text-sm" />
                          <input placeholder="Length" value={int.duration || ''} onChange={(e) => handleInterventionField(row.id, idx, 'duration', e.target.value)} className="rounded border px-2 py-1 text-sm" />
                          <input placeholder="Ratio" value={int.ratio || ''} onChange={(e) => handleInterventionField(row.id, idx, 'ratio', e.target.value)} className="rounded border px-2 py-1 text-sm" />
                        </>
                      ) : (
                        <p className="col-span-4 text-sm">{[int.name, int.frequency, int.duration, int.ratio].filter(Boolean).join(' • ') || 'Not recorded'}</p>
                      )}
                    </div>
                  ))}
                  {editing && (
                    <button type="button" onClick={() => addInterventionToRow(row.id)} className="text-xs text-indigo-600 hover:underline">+ Add intervention row</button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-2">
                  <label className="text-xs">
                    Outcome (review)
                    {editing ? (
                      <textarea value={row.outcome || ''} onChange={(e) => handleRowField(row.id, 'outcome', e.target.value)} rows={2} className="mt-1 w-full rounded border px-2 py-1 text-sm" />
                    ) : (
                      <p className="text-sm mt-1">{row.outcome || 'Not recorded'}</p>
                    )}
                  </label>
                  <label className="text-xs">
                    Achieved?
                    {editing ? (
                      <select value={row.achieved || ''} onChange={(e) => handleRowField(row.id, 'achieved', e.target.value)} className="mt-1 w-full rounded border px-2 py-1 text-sm">
                        {ACHIEVED_OPTIONS.map((o) => (
                          <option key={o} value={o}>{o || 'Not set'}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-sm mt-1">{row.achieved || 'Not recorded'}</p>
                    )}
                  </label>
                </div>
                {editing && (
                  <button type="button" onClick={() => removeRow(row.id)} className="text-xs text-rose-600 hover:underline">Remove row</button>
                )}
              </div>
            ))}
          </div>
        )}

        {editing && (
          <button type="button" onClick={addRow} className="text-sm text-indigo-600 hover:underline">+ Add area row</button>
        )}
      </CardContent>
    </Card>
  );
}
